import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
  NgZone,
  Inject,
  HostBinding,
  ChangeDetectionStrategy,
  AfterViewChecked
} from '@angular/core';
import { trigger, style, animate, transition } from '@angular/animations';
import { Router, NavigationStart } from '@angular/router';
import { MenuItem } from '../menu-item.model';
import { Subscription, fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { NgScrollbar } from 'ngx-scrollbar';

@Component({
  standalone: false,
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
  animations: [
    trigger('showMenu', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)' }),
        animate('.35s ease-in-out', style({ transform: 'translateX(0)' }))
      ]),
      transition(':leave', [
        style({ transform: 'translateX(0)' }),
        animate('.35s ease-in-out', style({ transform: 'translateX(-100%)' }))
      ])
    ]),
    trigger('showSubMenu', [
      transition(':enter', [style({ left: '-210px' }), animate('.25s ease-out', style({ left: '*' }))]),
      transition(
        ':leave',
        [
          style({ left: '*', top: '{{ previousTop }}' }),
          animate('.25s ease-out', style({ left: '-210px', top: '{{ previousTop }}' }))
        ],
        { params: { previousTop: '*' } }
      )
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SideMenuComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Input() items: MenuItem[] = [];
  @Input() opened = false;
  @Input() topOffset = 0;
  subMenuItem: MenuItem;
  subMenuPreviousTop = 0;
  subMenuTop = 0;
  subMenuOverflowFix = 0;
  sidebarScrollTop = 0;
  private scrollElement: HTMLElement;
  /* eslint-disable */
  @ViewChild('subMenu', { static: false })
  private readonly subMenu: ElementRef;
  private readonly subscriptions: Subscription;

  @ViewChild('sidebarScroll') scrollbarRef!: ElementRef;
  @HostBinding() get class() {
    return this.opened ? 'opened' : '';
  }

  constructor(
    private readonly router: Router,
    private readonly zone: NgZone,
    @Inject(DOCUMENT) private readonly document: any,
    private readonly changeDetector: ChangeDetectorRef
  ) {
    this.subscriptions = new Subscription();
  }

  titleIsActive(item: MenuItem): boolean {
    return this.router.isActive(item.link, false);
  }

  titleClick(currentTarget: EventTarget, item: MenuItem) {
    // hide existing subMenu and continue when DOM is updated
    if (this.subMenuItem) {
      if (!item.opened) {
        this.zone.runOutsideAngular(() => setTimeout(() => this.titleClick(currentTarget, item)));
      }
      this.closeSubMenu();

      return;
    }
    const target = currentTarget as HTMLElement;
    item.opened = true;

    if (!this.scrollElement) {
      this.scrollElement = this.scrollbarRef.nativeElement;
    }
    this.subMenuItem = item;
    this.changeDetector.detectChanges();
    // calculate final position when subMenu is added to the DOM
    this.zone.runOutsideAngular(() =>
      setTimeout(() => {
        this.subMenuTop = target.offsetTop + target.offsetHeight / 2 - this.scrollElement.scrollTop - 20;
        const subMenuHeight = this.subMenu.nativeElement.offsetHeight;
        this.subMenuTop -= subMenuHeight / 2;
        const subMenuBottom = this.scrollElement.offsetHeight - this.subMenuTop - subMenuHeight;
        this.subMenuOverflowFix = 0;
        if (subMenuBottom < 30) {
          this.subMenuOverflowFix = 30 - subMenuBottom;
        }
        if (this.subMenuTop < 15) {
          this.subMenuOverflowFix = this.subMenuTop - 15;
        }
        this.subMenuTop -= this.subMenuOverflowFix;
        // in particular scenrio, the value was -ve hence the menu-item was hidden
        if (this.subMenuTop < 0) {
          this.subMenuTop = -this.subMenuTop;
        }
        this.changeDetector.detectChanges();
      })
    );
  }

  closeSubMenu(detectChanges = false) {
    this.subMenuPreviousTop = this.subMenuTop;
    delete this.subMenuItem;
    this.items = this.items.map(item => Object.assign(item, { opened: false }));
    // PerfectScrollbarComponent detects changes earlier, need to repeat
    if (detectChanges) {
      this.changeDetector.detectChanges();
    }
  }

  getSubTitleIdBySubTitleCode(child) {
    return child.code.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
  }

  menuItemTrackBy(item: MenuItem): string {
    return item.code;
  }

  ngOnInit() {
    this.subscriptions.add(
      this.router.events.pipe(filter(event => event instanceof NavigationStart)).subscribe(_ => this.closeSubMenu())
    );
    this.registerClickOutside();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  ngAfterViewChecked(): void {
    // Access the native DOM element
    this.scrollElement = this.scrollbarRef.nativeElement;

    // You can now directly manipulate the DOM element, for example:
    this.scrollElement.style.overflowY = 'scroll';
  }

  private registerClickOutside() {
    this.zone.runOutsideAngular(() => {
      this.subscriptions.add(
        fromEvent<MouseEvent>(this.document, 'click')
          .pipe(filter(event => this.opened && this.scrollElement && !this.scrollElement.contains(event.target as Node)))
          .subscribe(() => this.closeSubMenu(true))
      );
    });
  }
}